// HomePage — Phase 6 Task 6.6-B 確定方針 G「ワンボタン作成 UX」.
//
// 主導線 = メイン CTA「ルームを作成する」(1 クリックで `POST /rooms` を即叩く).
// サブリンク = 「詳細設定で作成」(`/create` → 既存 CreateRoomPage).
// 「ログイン画面へ」 (既存ルームのオーナー復旧経路) も併設.
// 旧「ルームに参加 (URL 直入力)」フォームは廃止 (= URL / UUID を知ってる
// ユーザは直接開けばよく、HomePage 上の入力欄は冗長).
//
// Routing scheme (2026-05 改訂 — room URL は path-style に移行):
//   - `?action=create`   → App が即 createRoomShortcut → pathname `/<uuid>`
//   - pathname `/<uuid>` → ReJoinScreen
//   - `/create`     → CreateRoomPage    (戻るボタンで HomePage に戻る)
//   - 何も無し           → HomePage         (本コンポーネント)
//
// 1秒 UX を実現するため、メイン CTA 押下 → spinner 表示 → 成功なら URL を
// pathname `/<uuid>` に置換 (history.pushState + popstate) → ReJoinScreen 着地.
// 失敗時は inline error を表示し、CTA を再度有効化する.
//
// Phase 6 Task 6.6-B より前は本ページの「ルームを作成する」ボタンが
// `/create` に飛ぶ実装だった. Task 6.6-B で 1-click 路線に変更し、
// 詳細フォームは「詳細設定で作成」のサブリンク経由に格下げ.

import { useEffect, useState } from 'react';

import {
  CANVAS_SIZE_PRESETS,
  type CanvasSizePreset,
  DEFAULT_CANVAS_PRESET_ID,
  findCanvasPreset,
} from '../application/createRoomForm.js';
import { createRoomShortcut } from '../application/createRoomShortcut.js';
import type {
  ActiveRoomLookupResult,
  DeletedRoomLookupResult,
} from '../infrastructure/dispatcher/DispatcherClient.js';
import { AlphaWarningBanner } from './AlphaWarningBanner.js';
import { useSession } from './hooks/useSession.js';
import { formatReopenText } from './maintenanceText.js';
import { navigateToRoom, navigateToView } from './urlNavigation.js';

/**
 * メンテ probe (`getMaintenanceStatus`) の結果. `unknown` = 取得前 / 取得失敗
 * (= fail-silent でバナーを出さない). `open` = 平常. `maintenance` = メンテ中.
 */
type MaintenanceState =
  | { readonly t: 'unknown' }
  | { readonly t: 'open' }
  | { readonly t: 'maintenance'; readonly waitUntil: number | null };

export const HomePage = (): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // ワンクリック作成時の canvas size 選択. デフォルト = 標準 (真ん中).
  // 詳細サイズが欲しいユーザは「詳細設定で作成」サブリンクから既存
  // CreateRoomPage に飛ぶ流れを維持 (custom は HomePage には出さない).
  const [canvasPresetId, setCanvasPresetId] =
    useState<CanvasSizePreset['id']>(DEFAULT_CANVAS_PRESET_ID);

  // 「あなたのルーム」 section state. localStorage の admin_token 一覧を
  // `POST /admin/rooms/by-tokens` に通して active / deleted を取得.
  // - lookup 結果 (outcome === 'ok') のみ保持. それ以外 (rate-limited /
  //   invalid-body / network-error) は section 非表示にして fail-silent
  //   (主導線「作成」「ログイン画面へ」は別なので致命でない).
  // - `invalid` バケツの roomId は localStorage から自動 prune.
  // - 0 件なら section 自体非表示.
  // - 初期値 open=true (= default 開). ユーザ操作で閉じれるが永続化はしない.
  const [yourRoomsActive, setYourRoomsActive] = useState<readonly ActiveRoomLookupResult[]>([]);
  const [yourRoomsDeleted, setYourRoomsDeleted] = useState<readonly DeletedRoomLookupResult[]>([]);
  const [yourRoomsOpen, setYourRoomsOpen] = useState(true);

  // メンテ状態 probe. マウント時に 1 回 `getMaintenanceStatus()` を叩く.
  // `error` は fail-silent (= unknown のまま、バナーを出さない).
  const [maintenance, setMaintenance] = useState<MaintenanceState>({ t: 'unknown' });

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      const out = await dispatcher.getMaintenanceStatus();
      if (cancelled) return;
      if (out.outcome === 'open') {
        setMaintenance({ t: 'open' });
      } else if (out.outcome === 'maintenance') {
        setMaintenance({ t: 'maintenance', waitUntil: out.waitUntil });
      }
      // 'error' は fail-silent: unknown のまま (バナー非表示・作成可のまま).
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [dispatcher]);

  const isMaintenance = maintenance.t === 'maintenance';

  useEffect(() => {
    // マウント時に 1 回だけ. ルーム作成等で state が変わっても再 fetch しない
    // (= 作成直後はそもそも 1 件しかない情報を再 lookup する意味が薄く、UX 上
    // も navigate で離れる).
    let cancelled = false;
    const run = async (): Promise<void> => {
      const entries = adminTokens.list();
      if (entries.length === 0) return;
      const out = await dispatcher.lookupRoomsByTokens(
        entries.map((e) => ({ roomId: e.roomId, adminToken: e.token })),
      );
      if (cancelled) return;
      if (out.outcome !== 'ok') {
        // fail-silent. section は表示しないので致命的でない.
        // eslint-disable-next-line no-console
        console.warn('[HomePage] lookupRoomsByTokens failed:', out.outcome);
        return;
      }
      // server が「rooms にも deleted_rooms にも該当しなかった」と判定した
      // roomId は localStorage から prune (= stale entry を清掃).
      for (const roomId of out.invalid) {
        adminTokens.clear(roomId);
      }
      setYourRoomsActive(out.active);
      setYourRoomsDeleted(out.deleted);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [adminTokens, dispatcher]);

  const yourRoomsCount = yourRoomsActive.length + yourRoomsDeleted.length;

  const onCreateClick = async (): Promise<void> => {
    if (creating || isMaintenance) return;
    setCreating(true);
    setCreateError(null);
    // 念のため preset を再 lookup. 不明 id は標準にフォールバック (defensive).
    const preset = findCanvasPreset(canvasPresetId) ?? findCanvasPreset(DEFAULT_CANVAS_PRESET_ID);
    const out =
      preset === null
        ? await createRoomShortcut(dispatcher.createRoom)
        : await createRoomShortcut(dispatcher.createRoom, {
            canvasWidth: preset.width,
            canvasHeight: preset.height,
          });
    if (out.outcome === 'ok') {
      // Save admin_token to localStorage (確定方針 T) so RoomSettingsModal can
      // operate on this room from the same browser session.
      adminTokens.save(out.roomId, out.adminToken);
      // Navigate to ReJoinScreen via pathname `/<uuid>`.
      navigateToRoom(out.roomId);
      // Don't reset `creating` here — we're navigating away.
      return;
    }
    setCreateError(out.message);
    setCreating(false);
  };

  const onAdvancedClick = (): void => {
    if (isMaintenance) return;
    navigateToView('create');
  };

  const onLoginClick = (): void => {
    if (isMaintenance) return;
    navigateToView('login');
  };

  const onEnterRoomClick = (roomId: string): void => {
    if (isMaintenance) return;
    navigateToRoom(roomId);
  };

  const onClearDeletedRow = (roomId: string): void => {
    adminTokens.clear(roomId);
    setYourRoomsDeleted((prev) => prev.filter((r) => r.roomId !== roomId));
  };

  return (
    // 旧版 (= 2026-05-23 公開直前) では画面最上部に AlphaWarningBanner を帯で
    // 配置していたが、コンテンツパネルが画面中央のため視線が繋がらず目立たない
    // フィードバックで、タイトル直下のインライン警告に変更. 構造は元の
    // `grid place-items-center` 単段に戻す.
    // p-4 + w-full max-w: 固定 w-[440px] はスマホ縦 (390/360px) で viewport を超える
    // (2026-07-03 実機 fb、ErrorScreen と同パターン)。デスクトップは max-w 同値で不変。
    <div className="grid h-full place-items-center overflow-y-auto p-4">
      <section
        className="w-full max-w-[440px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
        aria-label="ホーム"
      >
        <h1 className="mb-3 text-center text-lg font-bold tracking-wide text-[var(--color-accent)]">
          タカミンお絵描きチャット
        </h1>
        <AlphaWarningBanner />

        {/* ─── メンテナンス中バナー ─────────────────────────────────────────
            `getMaintenanceStatus()` が maintenance を返したときだけ表示.
            赤帯 (MaintenanceBanner と同トーン) + 再開予定時刻 (JST). 作成系
            ボタンは下で disabled にする. */}
        {isMaintenance && (
          <div
            role="status"
            aria-live="polite"
            className="mb-4 rounded border border-[var(--color-error)] bg-[var(--color-error)]/90 px-3 py-2.5 text-center text-white shadow"
          >
            <p className="text-sm font-bold">🛠 ただいまメンテナンス中です</p>
            <p className="mt-1 text-xs">{formatReopenText(maintenance.waitUntil, Date.now())}</p>
            <p className="mt-1.5 text-[11px] opacity-90">
              再開時刻まで入室・ルーム作成・ログインはできません
            </p>
          </div>
        )}

        {/* ─── 1. ワンクリック作成 (メイン CTA) ─────────────────────────────── */}
        <button
          type="button"
          onClick={() => {
            void onCreateClick();
          }}
          disabled={creating || isMaintenance}
          className="mb-2 w-full rounded bg-[var(--color-accent)] px-4 py-3 text-base font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-60"
        >
          {creating ? '作成中…' : 'ルームを作成する'}
        </button>

        {/* ─── 1a. キャンバスサイズ selector ──────────────────────────────────
            主 CTA 直下に 3 タイプ (小 / 標準 / 大) を横並びで配置.
            デフォルトは「標準」が pre-selected. 詳細サイズは「詳細設定で
            作成」サブリンク経由 (custom は HomePage に出さない). */}
        <fieldset className="mb-2 border-0 p-0">
          <legend className="mb-1 block text-center text-xs text-[var(--color-text-muted)]">
            キャンバスサイズ
          </legend>
          <div className="flex gap-1.5">
            {CANVAS_SIZE_PRESETS.map((preset) => {
              const selected = preset.id === canvasPresetId;
              return (
                <button
                  key={preset.id}
                  type="button"
                  aria-pressed={selected}
                  disabled={creating || isMaintenance}
                  onClick={() => {
                    setCanvasPresetId(preset.id);
                  }}
                  className={
                    selected
                      ? 'flex-1 rounded border-2 border-[var(--color-accent)] bg-[var(--color-accent-soft)] px-2 py-1.5 text-xs font-bold text-[var(--color-panel)] transition disabled:opacity-60'
                      : 'flex-1 rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1.5 text-xs font-semibold text-white/80 transition hover:border-white/60 hover:text-white disabled:opacity-50'
                  }
                >
                  <span className="block leading-tight">{preset.label}</span>
                  <span className="block text-[10px] font-normal opacity-80 leading-tight">
                    {preset.width}²
                  </span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <p className="mb-1 text-center text-xs text-[var(--color-text-muted)]">
          1 クリックで作成・即入室。設定は入室後に変更できます。
        </p>

        {/* サブリンク = 詳細設定で作成 */}
        <div className="mb-6 text-center">
          <button
            type="button"
            onClick={onAdvancedClick}
            disabled={creating || isMaintenance}
            className="text-xs text-[var(--color-accent-soft)] underline transition hover:brightness-110 disabled:opacity-60"
          >
            詳細設定で作成
          </button>
        </div>

        {createError !== null && (
          <p className="mb-4 text-center text-sm text-[var(--color-error)]" role="alert">
            {createError}
          </p>
        )}

        {/* ─── 1b. あなたのルーム (localStorage の admin_token 逆引き) ──────
            localStorage の admin_token を `POST /admin/rooms/by-tokens` で
            bulk lookup し、active / deleted を分けて表示. 0 件 / lookup 失敗
            なら section 自体を出さない (= 主導線「作成」「ログイン画面へ」を
            邪魔しない). */}
        {yourRoomsCount > 0 && (
          <section
            className="mb-4 rounded border border-white/10 bg-[var(--color-bg-soft)] p-2"
            aria-label="あなたのルーム"
          >
            <button
              type="button"
              onClick={() => {
                setYourRoomsOpen((v) => !v);
              }}
              aria-expanded={yourRoomsOpen}
              className="flex w-full items-center justify-between rounded px-1 py-0.5 text-left text-xs font-bold text-white/90 hover:text-white"
            >
              <span>あなたのルーム ({yourRoomsCount} 件)</span>
              <span className="text-xs text-[var(--color-text-muted)]">
                {yourRoomsOpen ? '▾' : '▸'}
              </span>
            </button>
            {yourRoomsOpen && (
              <ul className="mt-1.5 flex flex-col gap-1">
                {yourRoomsActive.map((room) => (
                  <li
                    key={room.roomId}
                    className="flex items-center gap-2 rounded bg-[var(--color-panel)] px-2 py-1.5"
                  >
                    <span
                      className="flex-1 truncate text-sm text-white"
                      title={`${room.name} — ${room.roomId}`}
                    >
                      {room.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        onEnterRoomClick(room.roomId);
                      }}
                      disabled={creating || isMaintenance}
                      className="rounded bg-[var(--color-accent)] px-2 py-1 text-xs font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-60"
                    >
                      入室
                    </button>
                  </li>
                ))}
                {yourRoomsDeleted.map((room) => {
                  const statusText =
                    room.reason === 'soft_deleted' ? 'ユーザーが削除' : '長期未使用で期限切れ';
                  return (
                    <li
                      key={room.roomId}
                      className="flex items-center gap-2 rounded bg-[var(--color-panel)] px-2 py-1.5 opacity-70"
                    >
                      <div className="flex flex-1 flex-col">
                        <span
                          className="truncate text-sm text-white/70 line-through"
                          title={`${room.name} — ${room.roomId}`}
                        >
                          {room.name}
                        </span>
                        <span className="text-xs text-[var(--color-text-muted)]">{statusText}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          onClearDeletedRow(room.roomId);
                        }}
                        disabled={creating}
                        className="rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-xs font-semibold text-white/80 transition hover:border-white/60 hover:text-white disabled:opacity-50"
                      >
                        ローカル情報を消す
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* ─── 2. ログイン画面へ (既存ルームのオーナー復旧経路) ────────────── */}
        <p className="mb-2 text-center text-xs text-[var(--color-text-muted)]">
          既存ルームのオーナー操作はこちら
        </p>
        <button
          type="button"
          onClick={onLoginClick}
          disabled={creating || isMaintenance}
          className="w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-base font-bold text-white transition hover:bg-[var(--color-bg)] disabled:opacity-50"
        >
          ログイン画面へ
        </button>
      </section>
    </div>
  );
};
